<template>
  <div v-if="loading" class="flex justify-center items-center min-h-screen">
    <div class="text-gray-600">Carregando...</div>
  </div>

  <div v-else-if="error" class="flex justify-center items-center min-h-screen">
    <div class="text-red-600">{{ error }}</div>
  </div>

  <template v-else>
    <IntermediateScreen
      v-if="showIntermediateScreen"
      :redirectLink="sanitizeUrl(redirectLink)"
    />

    <div 
      class="flex justify-center items-start min-h-screen p-4 sm:p-6"
      :style="{ backgroundColor: validateColor(company.colorpage) || '#D3D3D3' }"
    >
      <div class="bg-white shadow-lg rounded-lg p-4 w-full max-w-sm sm:max-w-md md:max-w-lg">
        <div class="logo-container">
          <img
            v-if="company.logofilename"
            :src="getSecureLogoUrl(company.logofilename)"
            :alt="sanitizeText(company.name)"
            @error="handleImageError"
            class="w-40 h-auto mx-auto"
          />
        </div>

        <h1
          v-if="!company.hide_name"
          class="text-gray-700 font-semibold text-xl sm:text-2xl md:text-3xl mb-8 text-center"
        >
          {{ sanitizeText(company.name) }}
          
        </h1>

        <span v-if="company.id_branch && company.id_branch !== 0 && company.show_branch_name"
        class="block text-gray-400 text-base font-normal mt-1 text-center">
            {{ sanitizeText(company.branch_name) }}
        </span>

        <h1 class="text-gray-700 font-semibold text-lg mb-4 text-center sm:text-left mt-8">
          Sua Opinião é Muito Importante
        </h1>

        <p class="text-gray-600 mb-4 mt-8 text-center sm:text-left">
          De 0 a 10, quanto você nos recomendaria para um amigo ou parente?
        </p>

        <div class="flex flex-col items-center mb-4">
          <div class="flex gap-1 justify-center max-w-full overflow-x-auto">
            <button
              v-for="n in 11"
              :key="n"
              :class="[
                'border border-gray-300 rounded-lg text-center transition-colors duration-200',
                'w-8 sm:w-10 md:w-12 px-1 py-1 sm:px-2 sm:py-1',
                score === n - 1 ? 'bg-blue-500 text-white' : 'hover:bg-black hover:text-white'
              ]"
              @click="setScore(n - 1)"
              :disabled="submitting"
            >
              {{ n - 1 }}
            </button>
          </div>

          <div class="flex justify-between w-full max-w-xs sm:max-w-md md:max-w-lg text-gray-500 text-xs mt-1 px-1">
            <span class="text-left">Muito Improvável</span>
            <span class="text-right">Extremamente Provável</span>
          </div>
        </div>

        <div class="flex justify-center mt-6">
          <button 
            class="text-white font-semibold rounded-lg text-base px-4 py-2 hover:bg-blue-600 sm:text-lg sm:px-6"
            @click="submitScore"
            :disabled="!isValidScore || submitting"
            :style="{ backgroundColor: validateColor(company.colorbutton) || '#0A66C2' }"
          >
            {{ submitting ? 'Enviando...' : 'Confirmar' }}
          </button>
        </div>

        <div class="text-xs text-gray-500 text-right mt-6">
          <p v-if="company.footer_credit">
            Feito com ❤️ por Avalioo
          </p>
        </div>
      </div>
    </div>
  </template>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import IntermediateScreen from './IntermediateScreen.vue';
//import axios from 'axios';
//import config from '@/config';
// import { sanitizeText, sanitizeUrl, validateColor, sanitizeFilename } from '@/utils/security';
import { sanitizeText, sanitizeUrl, validateColor } from '@/utils/security';
import { useRouter } from 'vue-router';  // Adicione esta linha
import { useCompanyStore } from '@/stores/company';
import npsService from '@/services/nps.service';
//import { notificationService } from '../services/notifications/NotificationService';
//import { NotificationService } from '@/services/notifications/NotificationService';
//>>>import { notificationService } from '@/services/notifications/NotificationService';



export default {
  name: 'NPSForm',
  components: { 
    IntermediateScreen 
  },

  props: {
    slug: {
      type: String,
      default: null,
      validator: value => !value || /^[a-z2-9]{3}$/.test(value)
    },
    custom_link: {
      type: String,
      default: null,
      validator: value => !value || /^[a-zA-Z0-9-_]{3,50}$/.test(value)
    },
    branch: {  // Nova prop para branch
      type: String,
      default: null,
      validator: value => !value || /^\d+$/.test(value)
    }
  },

  setup(props) {
    const router = useRouter();  // Adicione esta linha
    const loading = ref(true);
    const error = ref(null);
    const company = ref({});
    const score = ref(null);
    const submitting = ref(false);
    const redirectLink = ref(null);
    const showIntermediateScreen = ref(false);
    const apiBaseUrl = ref(process.env.VUE_APP_API_URL);
    const isValidScore = computed(() => 
      score.value !== null && score.value >= 0 && score.value <= 10
    );

    // Métodos de segurança
    const getSecureLogoUrl = (filename) => {
      if (!filename) return '';
      // Sanitiza o nome do arquivo
      const sanitizedFilename = filename.replace(/[^a-zA-Z0-9._-]/g, '');
      console.log("sanitizedFilename", sanitizedFilename);
      console.log("Filename", filename);
      console.log("getSecureLogoUrl", `${apiBaseUrl.value}/static/logos/${sanitizedFilename}`);
      return `${apiBaseUrl.value}/static/logos/${sanitizedFilename}`;
    };

    const handleImageError = (event) => {
      event.target.style.display = 'none';
      console.error('Erro ao carregar imagem');
    };

    // Métodos de cookie com segurança adicional
    const setCookie = (name, value, days) => {
      const expires = new Date();
      expires.setDate(expires.getDate() + days);
      document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires.toUTCString()}; path=/; Secure; SameSite=Strict`;
    };

    const getCookie = (name) => {
      try {
        const cookies = document.cookie.split('; ');
        const cookie = cookies.find(row => row.startsWith(`${name}=`));
        return cookie ? decodeURIComponent(cookie.split('=')[1]) : null;
      } catch (e) {
        console.error('Erro ao ler cookie:', e);
        return null;
      }
    };

    // Métodos principais
    const fetchCompanyData = async (slug = null, custom_link = null, shouldIncrement = true) => {
      try {
        loading.value = true;
        error.value = null;
        //let apiUrl = '';
        let response;

        console.log("slug and custom_link", slug, custom_link);

        // Validação dos parâmetros
        if (slug) {
          if (!/^[a-z2-9]{3}$/.test(slug)) {
            router.push({ name: 'not-found' });
            return;
          }
          response = await npsService.getCompanyBySlug(slug, shouldIncrement, props.branch);
          console.log (" response getcompanybyslug", response);
          //apiUrl = `${apiBaseUrl.value}/s/${encodeURIComponent(slug)}`;
        } else if (custom_link) {
          if (!/^[a-zA-Z0-9-_]{3,50}$/.test(custom_link)) {
            router.push({ name: 'not-found' });
            return;
          }
          response = await npsService.getCompanyByCustomLink(custom_link, shouldIncrement , props.branch);
          console.log ("response getcompanybyCustomLink", response);
          //apiUrl = `${apiBaseUrl.value}/c/${encodeURIComponent(custom_link)}`;
        } else {
          router.push({ name: 'not-found' });
          return;
        }

        //console.log ("response.data::", response.data);
        // console.log ("response::", response);
        // Verifica se a resposta tem os dados necessários
        if (response.data && response.data.company_id) {
          company.value = response.data;
        } else {
          await router.replace({ name: 'not-found' });
        }


      } catch (error) {
        
        console.error('Erro ao buscar dados:', error);
    
        // Se for erro 404, redireciona para not-found
        if (error.response && error.response.status === 404) {
          await router.replace({ name: 'not-found' });
          return;
        }
        
        // Para outros erros, mostra mensagem de erro mas não redireciona
        error.value = 'Não foi possível carregar os dados. Por favor, tente novamente.';
        
        
        // // Só loga erros que não são 404
        // if (!error.response || error.response.status !== 404) {
        //   console.error('Erro inesperado ao buscar dados:', error);
        //   error.value = 'Não foi possível carregar os dados. Por favor, tente novamente.';
        // } else {
        //   router.push({ name: 'not-found' });
        // }
      } finally {
        loading.value = false;
      }
    };

    const submitScore = async () => {
      if (!isValidScore.value || submitting.value) return;

      try {
        submitting.value = true;

        // Validação dos dados antes do envio
        if (!company.value?.company_id) {
          throw new Error('Dados da empresa inválidos');
        }


         // Garante que id_branch seja 0 para a matriz ou o id da unidade para outras unidades
        const branchId = company.value.id_branch || 0;

        // console.log("branchId::::",branchId);
        // Cria o review
        const reviewPayload = {
          id_company: parseInt(company.value.company_id),
          id_branch: branchId,  // Sempre terá um valor: 0 para matriz ou id para outras unidades
          nps: score.value,
          type: 0,
          review: ''
        };

        const reviewsResponse = await npsService.submitReview(reviewPayload);
        const linkResponse = await npsService.getRedirectLink(score.value, company.value.company_id, branchId);
        console.log("linkResponse", linkResponse);

        const { redirect, intermediate, link } = linkResponse.data;
        console.log("link >>>>>>>", link)
        const companyStore = useCompanyStore();
        companyStore.setTempCompany(company.value);
        // console.log ("Company Store....: ", company.value);
        console.log ("Link Redirect....: ", link);



        switch (redirect) {
            case "1": 
                //redirect to Google Review - with Warning Google Intermediate Screen          
                if (intermediate && link) {
                  if (!sanitizeUrl(link)) {
                    throw new Error('Link de redirecionamento inválido');
                  }

                  router.push({
                      name: 'IntermediateScreen', 
                      query: { 
                        link: link, 
                      }
                  });
                  //redirectLink.value = link;
                  //showIntermediateScreen.value = true;
                } else if (sanitizeUrl(link)) {
                  window.location.href = sanitizeUrl(link);
                }
                break;


            case "2": 
                //redirect to Internal Form
                router.push({
                  name: 'InternalForm',
                  query: {
                    nps: score.value,
                    review_id: reviewsResponse.data.id
                  }
                });
                break;


            case "3":
                //redirect to Thank You Page

                router.push({
                  name: 'ThankYouPage'
                });
                break;


            case "4":
                //redirect to external link, without Intermediate Screen
                if (link) {
                  if (!sanitizeUrl(link)) {
                    throw new Error('Link de redirecionamento inválido');
                  }
                  
                  // try {
                      
                  // } catch (error) {
                  //     console.error('Erro ao enviar notificação:', error);
                  // }

                  window.location.href = sanitizeUrl(link);
                }
                break;
            default:
                console.log ('redirect option invalid!');
                break

        }


      } catch (e) {
        console.error('Erro na submissão:', e);
        error.value = 'Ocorreu um erro ao enviar sua avaliação. Por favor, tente novamente.';
      } finally {
        submitting.value = false;
      }
    };

    // Lifecycle hooks
    onMounted(() => {
      
      console.log("slug and custom_link", props.slug, props.custom_link)
      const identifier = props.slug || props.custom_link;
      const cookieKey = `access_${identifier}`;
      const accessCookie = getCookie(cookieKey);
      const today = new Date().toISOString().split('T')[0];

      fetchCompanyData(props.slug, props.custom_link, accessCookie !== today)
        .then(() => {
          if (accessCookie !== today) {
            setCookie(cookieKey, today, 1);
          }
        });
    });

    return {
      loading,
      error,
      company,
      score,
      submitting,
      redirectLink,
      showIntermediateScreen,
      apiBaseUrl,
      isValidScore,
      setScore: (value) => score.value = value,
      submitScore,
      handleImageError,
      sanitizeText,
      sanitizeUrl,
      validateColor,
      getSecureLogoUrl
    };
  }
};
</script>

<style scoped>
.company-logo {
  max-width: 100px;
  height: auto;
  margin-bottom: 20px;
}

a {
  color: #00b2d5;
  text-decoration: none;
}

a:hover {
  text-decoration: underline;
}
</style>